import { useState } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '@/lib/supabase';
import { useFuncionarios } from '@/context/FuncionariosContext';
import { useModalAnimation } from '@/hooks/useModalAnimation';
import { useModalOverflow } from '@/hooks/useModalOverflow';

interface Progressao {
  id: string;
  funcionarioId: string;
  funcionarioNome: string;
  dataProgressao: string;
  nivelAnterior: number;
  nivelNovo: number;
  pontosDescontados: number;
  observacoes: string | null;
}

interface EditarProgressaoModalProps {
  progressao: Progressao;
  onClose: () => void;
  onSuccess: (updated: Progressao) => void;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  const [year, month, day] = dateStr.split('-');
  if (!year || !month || !day) return dateStr;
  return `${day}/${month}/${year}`;
}

export default function EditarProgressaoModal({ progressao, onClose, onSuccess }: EditarProgressaoModalProps) {
  const { funcionarios, updateFuncionario } = useFuncionarios();
  const { animating, handleClose } = useModalAnimation(onClose);
  useModalOverflow(true);

  const [dataProgressao, setDataProgressao] = useState(progressao.dataProgressao);
  const [nivelNovo, setNivelNovo] = useState(progressao.nivelNovo);
  const [observacoes, setObservacoes] = useState(progressao.observacoes ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const nivelMudou = nivelNovo !== progressao.nivelNovo;
  const dataMudou = dataProgressao !== progressao.dataProgressao;
  const obsMudou = observacoes !== (progressao.observacoes ?? '');
  const temAlteracoes = nivelMudou || dataMudou || obsMudou;

  const initials = progressao.funcionarioNome
    .split(' ')
    .filter((_, i, arr) => i === 0 || i === arr.length - 1)
    .map((n) => n[0])
    .join('')
    .toUpperCase();

  async function handleGuardar() {
    if (!dataProgressao) { setError('Data de progressão obrigatória.'); return; }
    if (nivelNovo <= progressao.nivelAnterior) {
      setError(`O novo nível deve ser superior ao nível anterior (${progressao.nivelAnterior}).`);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const { error: updErr } = await supabase
        .from('progressoes')
        .update({
          data_progressao: dataProgressao,
          nivel_novo: nivelNovo,
          observacoes: observacoes || null,
        })
        .eq('id', progressao.id);
      if (updErr) throw new Error(updErr.message);

      if (nivelMudou || dataMudou) {
        const func = funcionarios.find((f) => f.id === progressao.funcionarioId);
        if (func) {
          const { data: todasProgressoes } = await supabase
            .from('progressoes')
            .select('id, data_progressao, nivel_novo')
            .eq('funcionario_id', progressao.funcionarioId)
            .order('data_progressao', { ascending: false });

          const maisRecente = todasProgressoes && todasProgressoes.length > 0 ? todasProgressoes[0] : null;
          const estaEhMaisRecente = maisRecente?.id === progressao.id;

          if (estaEhMaisRecente) {
            await updateFuncionario({
              ...func,
              nivelAtual: nivelNovo,
              novoNivel2025: nivelNovo,
              ultimaProgressao: dataProgressao,
            }, func.id);
          } else if (dataMudou) {
            const novaDataMaisRecente = todasProgressoes?.sort((a, b) =>
              new Date(b.data_progressao as string).getTime() - new Date(a.data_progressao as string).getTime()
            )[0];
            if (novaDataMaisRecente?.id === progressao.id) {
              await updateFuncionario({
                ...func,
                nivelAtual: nivelNovo,
                novoNivel2025: nivelNovo,
                ultimaProgressao: dataProgressao,
              }, func.id);
            }
          }
        }
      }

      const updated: Progressao = {
        ...progressao,
        dataProgressao,
        nivelNovo,
        observacoes: observacoes || null,
      };
      onSuccess(updated);
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erro ao guardar alterações.');
    } finally {
      setSaving(false);
    }
  }

  const modal = (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" onClick={handleClose}>
      <div className={`absolute inset-0 bg-black/50 backdrop-blur-sm ${animating ? 'modal-backdrop-active' : 'modal-backdrop-enter'}`} />
      <div
        className={`relative bg-white rounded-2xl w-full max-w-5xl overflow-hidden border border-zinc-100 flex flex-col max-h-[96vh] ${animating ? 'modal-panel-active' : 'modal-panel-enter'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-zinc-100 flex items-center justify-between flex-shrink-0 bg-zinc-50/60">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-100 flex-shrink-0">
              <i className="ri-edit-line text-amber-600 text-lg" />
            </div>
            <div>
              <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest mb-0.5">Editar</p>
              <h2 className="text-[15px] font-bold text-zinc-900 leading-tight">Progressão</h2>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Funcionário */}
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center gap-3 bg-white flex-shrink-0">
          <div className="w-9 h-9 flex items-center justify-center rounded-full bg-zinc-900 text-white text-[12px] font-bold flex-shrink-0">
            {initials}
          </div>
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-zinc-800 truncate">{progressao.funcionarioNome}</p>
            <p className="text-[11px] text-zinc-400">Progressão original: {formatDate(progressao.dataProgressao)}</p>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">

          {/* Dados originais */}
          <div>
            <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest mb-3">Dados Originais</p>
            <div className="bg-zinc-50 rounded-xl border border-zinc-100 overflow-hidden">
              <div className="grid grid-cols-3 divide-x divide-zinc-100">
                <div className="p-3 text-center">
                  <p className="text-[10px] text-zinc-400 mb-1">Data</p>
                  <p className="text-[12px] font-bold text-zinc-700">{formatDate(progressao.dataProgressao)}</p>
                </div>
                <div className="p-3 text-center">
                  <p className="text-[10px] text-zinc-400 mb-1">Nível</p>
                  <p className="text-[12px] font-bold text-zinc-700">{progressao.nivelAnterior} → {progressao.nivelNovo}</p>
                </div>
                <div className="p-3 text-center">
                  <p className="text-[10px] text-zinc-400 mb-1">Pts descontados</p>
                  <p className="text-[12px] font-bold text-rose-600">{progressao.pontosDescontados} pts</p>
                </div>
              </div>
            </div>
          </div>

          {/* Aviso pontos */}
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-100 rounded-xl p-3.5">
            <div className="w-5 h-5 flex items-center justify-center text-amber-500 flex-shrink-0 mt-0.5">
              <i className="ri-information-line text-sm" />
            </div>
            <p className="text-[11px] text-amber-700 leading-relaxed">
              Os <strong>pontos descontados</strong> não são alterados aqui. Para reverter os pontos, use <strong>Anular</strong>.
            </p>
          </div>

          {/* Campos editáveis */}
          <div>
            <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest mb-3">Editar Dados</p>
            <div className="space-y-3">
              <div>
                <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-1.5">
                  Data da Progressão *
                  {dataMudou && <span className="ml-2 text-amber-500 normal-case font-normal">(alterado)</span>}
                </label>
                <input
                  type="date"
                  value={dataProgressao}
                  onChange={(e) => setDataProgressao(e.target.value)}
                  className={`w-full px-3 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-300 transition-colors ${dataMudou ? 'border-amber-300 bg-amber-50/30' : 'border-zinc-200'}`}
                />
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-1.5">
                  Novo Nível
                  {nivelMudou && <span className="ml-2 text-amber-500 normal-case font-normal">({progressao.nivelNovo} → {nivelNovo})</span>}
                </label>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2 text-[12px] text-zinc-500 bg-zinc-50 px-3 py-2.5 rounded-lg border border-zinc-200 flex-shrink-0">
                    <span className="font-semibold text-zinc-700">Nível {progressao.nivelAnterior}</span>
                    <div className="w-4 h-4 flex items-center justify-center text-zinc-400">
                      <i className="ri-arrow-right-line text-sm" />
                    </div>
                  </div>
                  <input
                    type="number"
                    min={progressao.nivelAnterior + 1}
                    max={50}
                    value={nivelNovo}
                    onChange={(e) => setNivelNovo(parseInt(e.target.value, 10) || progressao.nivelNovo)}
                    className={`flex-1 px-3 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-300 font-bold transition-colors ${nivelMudou ? 'border-amber-300 bg-amber-50/30' : 'border-zinc-200'}`}
                  />
                </div>
                <p className="text-[11px] text-zinc-400 mt-1">Mínimo: nível {progressao.nivelAnterior + 1}</p>
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-1.5">
                  Observações
                  {obsMudou && <span className="ml-2 text-amber-500 normal-case font-normal">(alterado)</span>}
                </label>
                <textarea
                  value={observacoes}
                  onChange={(e) => setObservacoes(e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="Motivo ou notas sobre a progressão..."
                  className={`w-full px-3 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-700 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300 resize-none transition-colors ${obsMudou ? 'border-amber-300 bg-amber-50/30' : 'border-zinc-200'}`}
                />
                <div className="flex items-center justify-between mt-1">
                  {temAlteracoes ? (
                    <span className="text-[11px] text-amber-600 font-medium flex items-center gap-1">
                      <i className="ri-edit-line text-xs" />
                      {[dataMudou && 'data', nivelMudou && 'nível', obsMudou && 'observações'].filter(Boolean).join(', ')} alterado(s)
                    </span>
                  ) : (
                    <span className="text-[11px] text-zinc-400">Sem alterações</span>
                  )}
                  <span className={`text-[11px] ${observacoes.length > 450 ? 'text-amber-500' : 'text-zinc-400'}`}>{observacoes.length}/500</span>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-[12px] text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2.5">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line text-sm" />
              </div>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-zinc-100 flex items-center justify-end gap-2.5 flex-shrink-0 bg-zinc-50/40">
          <button
            onClick={handleClose}
            className="whitespace-nowrap px-4 py-2 text-[13px] font-medium text-zinc-600 border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors cursor-pointer"
          >
            Cancelar
          </button>
          <button
            onClick={handleGuardar}
            disabled={saving || !temAlteracoes}
            className="whitespace-nowrap flex items-center gap-2 px-5 py-2 text-[13px] font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving
              ? <><i className="ri-loader-4-line animate-spin text-sm" />A guardar...</>
              : <><i className="ri-save-line text-sm" />{temAlteracoes ? 'Guardar Alterações' : 'Sem alterações'}</>
            }
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}